
import { useCallback, FormEvent } from "react";
import { toast } from "@/hooks/use-toast";
import { JobFormData } from "../types/jobTypes";

/**
 * Hook for handling form actions like input changes, selections, and submissions
 */
export const useJobFormHandlers = (
  jobDataRef: React.MutableRefObject<JobFormData>,
  setJobData: React.Dispatch<React.SetStateAction<JobFormData>>,
  setSelectedLanguages: React.Dispatch<React.SetStateAction<string[]>>,
  setShowOtherLanguageField: React.Dispatch<React.SetStateAction<boolean>>,
  selectedLanguages: string[],
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  onSave: (updatedJob: JobFormData) => Promise<boolean>
) => {
  const handleSelectCompany = useCallback((id: string, name: string) => {
    console.log(`Selected company: ${name} (${id})`);
    setJobData(prevData => ({
      ...prevData,
      companyId: id,
      companyName: name,
      contactId: undefined // Reset contact when company changes
    }));
  }, [setJobData]);

  const handleSelectContact = useCallback((contactId: string) => {
    console.log(`Selected contact: ${contactId}`);
    setJobData(prevData => ({
      ...prevData,
      contactId
    }));
  }, [setJobData]);
  
  const handleSkillsKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, field: keyof JobFormData) => {
    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
      e.preventDefault();
      const currentValue = jobDataRef.current[field] as string || "";
      const newValue = currentValue ? 
                       `${currentValue}, ${e.currentTarget.value.trim()}` : 
                       e.currentTarget.value.trim();
      
      console.log(`Adding ${field}: ${e.currentTarget.value.trim()}`);
      setJobData(prevData => ({
        ...prevData,
        [field]: newValue
      }));
      
      // Clear the input
      e.currentTarget.value = '';
    }
  }, [jobDataRef, setJobData]);
  
  const handleLanguageChange = useCallback((language: string, checked: boolean) => {
    let newLanguages: string[];
    if (checked) {
      newLanguages = [...selectedLanguages, language];
    } else {
      newLanguages = selectedLanguages.filter(lang => lang !== language);
    }
    
    console.log(`Language changed: ${language}, checked: ${checked}`);
    console.log(`New languages: ${newLanguages.join(', ')}`);
    
    setSelectedLanguages(newLanguages);
    
    // Update showOtherLanguageField state
    setShowOtherLanguageField(newLanguages.includes("Other"));
    
    // Update the languageRequirements in jobData
    setJobData(prevData => ({
      ...prevData,
      languageRequirements: newLanguages.join(", ")
    }));
  }, [selectedLanguages, setSelectedLanguages, setShowOtherLanguageField, setJobData]);
  
  const handleEmploymentTypeChange = useCallback((type: string, checked: boolean) => {
    console.log(`Employment type changed: ${type}, checked: ${checked}`);
    setJobData(prevData => {
      let currentTypes = prevData.employmentType ? 
                      prevData.employmentType.split(',').map(t => t.trim()) : 
                      [];
      
      if (checked) {
        // Add the type if it's not already there
        if (!currentTypes.includes(type)) {
          currentTypes.push(type);
        }
      } else {
        // Remove the type
        currentTypes = currentTypes.filter(t => t !== type);
      }
      
      console.log(`New employment types: ${currentTypes.join(', ')}`);
      return {
        ...prevData,
        employmentType: currentTypes.join(", ")
      };
    });
  }, [setJobData]);

  // New handler for time commitment
  const handleTimeCommitmentChange = useCallback((value: string) => {
    console.log(`Time commitment changed: ${value}`);
    setJobData(prevData => ({
      ...prevData,
      timeCommitment: value
    }));
  }, [setJobData]);

  const handleInputChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
    field: keyof JobFormData
  ) => {
    const value = e.target.value;
    console.log(`Input changed for ${field}: ${value}`);
    
    setJobData(prevData => ({
      ...prevData,
      [field]: value
    }));
  }, [setJobData]);
  
  // For Select components from shadcn/ui
  const handleSelectChange = useCallback((value: string, field: keyof JobFormData) => {
    console.log(`Select changed for ${field}: ${value}`);
    setJobData(prevData => ({
      ...prevData,
      [field]: value
    }));
  }, [setJobData]);

  const handleSubmit = useCallback(async (e: FormEvent, jobId?: string) => {
    console.log("handleSubmit called", { jobId, data: jobDataRef.current });
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Ensure we have required fields
      if (!jobDataRef.current.title || !jobDataRef.current.companyId) {
        console.error("Missing required fields: title or companyId");
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return false;
      }
      
      // Call the provided onSave function with the current job data
      console.log("Calling onSave with data:", jobDataRef.current);
      const success = await onSave(jobDataRef.current);
      console.log("onSave result:", success);
      
      if (success) {
        toast({
          title: "Success",
          description: jobId ? "Job offer has been updated successfully" : "Job offer has been created successfully",
        });
        return true;
      } else {
        console.error("Save operation returned false");
        toast({
          title: "Error",
          description: "Failed to save job offer. Please try again.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error(jobId ? "Error updating job:" : "Error creating job:", error);
      toast({
        title: "Error",
        description: jobId ? 
          "Failed to update job offer: " + (error instanceof Error ? error.message : "Unknown error") :
          "Failed to create job offer: " + (error instanceof Error ? error.message : "Unknown error"),
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [onSave, setIsSubmitting, jobDataRef]);

  const isEmploymentTypeSelected = useCallback((type: string) => {
    if (!jobDataRef.current.employmentType) return false;
    return jobDataRef.current.employmentType.split(',').map(t => t.trim()).includes(type);
  }, [jobDataRef]);

  return {
    handleSelectCompany,
    handleSelectContact,
    handleSkillsKeyDown,
    handleLanguageChange,
    handleEmploymentTypeChange,
    handleTimeCommitmentChange,
    handleInputChange,
    handleSelectChange,
    handleSubmit,
    isEmploymentTypeSelected
  };
};
